import { useState } from 'react';
import DatePicker, { DatePickerModel } from '../../components/date-picker/DatePicker';
import VariationPicker, { VariationItemModel, VariationPickerModel } from '../../components/variation-picker/VariationPicker';
import TimePicker, { TimePickerModel } from '../../components/time-picker/TimePicker';
import ServiceUserGate from '../service-user-gate/ServiceUserGate';
import { OrderInfo, OrderInfoContext } from '@src/app/order-info-context';
import { OrderFlowCloseReason, OrderFlowContext, OrderFlowData } from '@src/app/order-flow-context';
import FullModal from '@src/components/modal/FullModal';
import AnyOfTheseItems, { AnyOfTheseItemsModel } from '@src/blocks/any-of-these-items/AnyOfTheseItems';
import UploadPhotos, { UploadPhotosModel } from '../../components/upload-photos/UploadPhotos';
import SizeMeasurement, { SizeMeasurementModel } from '@src/components/size-measurement/SizeMeasurement';

export enum StepType {
  userGate = 'user-gate',
  variationPicker = 'variation-picker',
  timePicker = 'time-picker',
  datePicker = 'date-picker',
  itemsQuery = 'items-query',
  uploadPhoto = 'upload-photo',
  sizeMeasurement = 'size-measurement',
}

export interface Step {
  type:
    | 'user-gate'
    | 'variation-picker'
    | 'time-picker'
    | 'date-picker'
    | 'items-query'
    | 'upload-photo'
    | 'size-measurement'
    | 'item-service-picker';
  stepLabel: string;
  [key: string]: any;
}

export interface OrderFlowModel {
  postcode: string;
  serviceId: number | string;
  steps: Step[];
  initial?: OrderInfo;
  onOrderFlowClose: (orderInfo?: OrderInfo, reason?: string) => any;
}

const OrderFlow = ({ initial, steps, onOrderFlowClose, ...model }: OrderFlowModel) => {
  const [closeClicked, setCloseClicked] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasInfo, setHasInfo] = useState(false);

  const [postcode, setPostcode] = useState(model.postcode);
  const [firstCollection, setFirstCollection] = useState(initial?.firstCollection);
  const [groupId, setGroupId] = useState(initial?.groupId);
  const [productCode, setProductCode] = useState(initial?.productCode);
  const [quantity, setQuantity] = useState(initial?.quantity ?? -1);
  const [schedule, setSchedule] = useState(initial?.schedule?.map((s) => s));
  const [serviceId, setServiceId] = useState(model.serviceId);
  const [session, setSession] = useState(initial?.session);
  const [variantId, setVariantId] = useState(initial?.variantId);
  const [haveSuggestedItems, setHaveSuggestedItems] = useState(false);
  const [uploadPhotos, setUploadPhotos] = useState(initial?.uploadPhotos);
  const [description, setDescription] = useState(initial?.description);
  const [bidAllocationProduct, setBidAllocationProduct] = useState(initial?.bidAllocationProduct);
  const [selectedItem, setSelectedItem] = useState<VariationItemModel>();

  const orderInfo: OrderInfo = {
    firstCollection,
    groupId,
    postcode,
    productCode,
    description,
    quantity,
    schedule,
    serviceId,
    session,
    variantId,
    uploadPhotos,
    bidAllocationProduct,
    selectedItem,
    setFirstCollection,
    setGroupId,
    setPostcode,
    setProductCode,
    setDescription,
    setQuantity,
    setSchedule,
    setServiceId,
    setSession,
    setVariantId,
    haveSuggestedItems,
    setHaveSuggestedItems,
    setUploadPhotos,
    setBidAllocationProduct,
    setSelectedItem,
  };

  const getStepElement = () => {
    const type = steps && stepIndex < steps.length ? steps[stepIndex].type : null;
    switch (type) {
      case StepType.userGate:
        return <ServiceUserGate data={steps[stepIndex]} />;
      case StepType.variationPicker:
        return <VariationPicker data={steps[stepIndex] as unknown as VariationPickerModel} />;
      case StepType.timePicker:
        return <TimePicker data={steps[stepIndex] as unknown as TimePickerModel} />;
      case StepType.datePicker:
        return <DatePicker data={steps[stepIndex] as unknown as DatePickerModel} />;
      case StepType.itemsQuery:
        return <AnyOfTheseItems data={steps[stepIndex] as unknown as AnyOfTheseItemsModel} />;
      case StepType.uploadPhoto:
        return <UploadPhotos data={steps[stepIndex] as unknown as UploadPhotosModel} />;
      case StepType.sizeMeasurement:
        return <SizeMeasurement data={steps[stepIndex] as unknown as SizeMeasurementModel} />;
      default:
        handleContinue();
        return <></>;
    }
  };

  const handleBack = () => {
    const step = stepIndex - 1 >= 0 ? stepIndex - 1 : 0;
    setStepIndex(step);
  };

  const handleContinue = () => {
    if (stepIndex + 1 == steps.length) {
      setCloseClicked(true);
      setHasInfo(true);
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const handleClose = (reason?: string) => {
    onOrderFlowClose(undefined, reason);
    setCloseClicked(true);
  };

  const handleModalClose = () => {
    if (hasInfo) {
      onOrderFlowClose(orderInfo);
    } else {
      onOrderFlowClose();
    }
  };

  const element = getStepElement();

  const orderFlowData: OrderFlowData = {
    handleBack,
    handleContinue,
    handleClose,
  };

  const hasBack = (stepIndex > 0 && steps[0].type !== StepType.userGate) || (stepIndex > 1 && steps[0].type === StepType.userGate);
  const progress =
    stepIndex >= 0 && stepIndex < steps.length && steps[stepIndex].type !== StepType.userGate
      ? steps[0].type === StepType.userGate
        ? (stepIndex / (steps.length - 1)) * 100
        : ((stepIndex + 1) / steps.length) * 100
      : undefined;

  const hasUserGate = steps[0].type === StepType.userGate;
  const itemWidth = hasUserGate ? (1 / (steps.length - 1)) * 100 : (1 / steps.length) * 100;

  return (
    <OrderInfoContext.Provider value={orderInfo}>
      <OrderFlowContext.Provider value={orderFlowData}>
        <FullModal
          progress={progress}
          onBack={handleBack}
          onClose={handleModalClose}
          hasBack={hasBack}
          closeClicked={closeClicked}
          hasUserGate={hasUserGate}
          itemWidth={itemWidth}
          steps={steps}
          stepIndex={stepIndex}
        >
          {element}
        </FullModal>
      </OrderFlowContext.Provider>
    </OrderInfoContext.Provider>
  );
};

export default OrderFlow;
